/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { FILTERS_QUERY_PARAM, type FILTERS_CLOSED, type FILTERS_OPEN } from '@/hooks/use-filters-panel-state';

/** The breakpoint at which the panel stops being an overlay and becomes a sidebar (`lg`). */
const NARROW_QUERY = '(max-width: 63.999rem)';

/**
 * `default` is the absence of the query parameter — the state that lets the breakpoint decide.
 * The two named states are the shopper's explicit choice and mean the same thing at every width.
 */
export const PANEL_DEFAULT = 'default';
export type PanelState = typeof FILTERS_OPEN | typeof FILTERS_CLOSED | typeof PANEL_DEFAULT;

/**
 * The panel's declared state, including "the shopper has not said".
 *
 * Kept distinct from `useFiltersPanelState`'s boolean because the boolean cannot express the
 * difference between "closed" and "unstated", and the whole CSS default depends on that
 * difference. See the note on `CategoryRefinementsPanel`.
 */
export function usePanelState(): PanelState {
    const location = useLocation();
    return (new URLSearchParams(location.search).get(FILTERS_QUERY_PARAM) ?? PANEL_DEFAULT) as PanelState;
}

/**
 * True below `lg`. Starts `false` so the first client render matches the server's, then settles
 * from `matchMedia`.
 *
 * Only things that are *not* server-rendered layout may read this: the overlay (client-only by
 * construction, because it is lazily mounted on first interaction) and the Filters control's
 * pressed state (an attribute, not a box). The sidebar itself must stay CSS-gated, or the default
 * would flip after hydration and shift the layout.
 */
export function useIsNarrow(): boolean {
    const [isNarrow, setIsNarrow] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia(NARROW_QUERY);
        const sync = () => setIsNarrow(mq.matches);
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);
    return isNarrow;
}

/**
 * Whether the panel is actually showing, for the Filters control's `aria-pressed` and for the
 * grid's panel-aware image widths.
 *
 * The tri-state means the raw `filtersOpen` boolean is wrong at desktop: with the parameter absent
 * the sidebar is open but the shopper has made no explicit choice, so a control reporting
 * `aria-pressed={filtersOpen}` would announce "not pressed" over an open panel. Reading the
 * breakpoint fixes it. Before hydration this reports the narrow answer and then corrects — an
 * attribute correction, not a reflow, so it costs no layout stability.
 */
export function useFiltersVisible(filtersOpen: boolean): boolean {
    const panelState = usePanelState();
    const isNarrow = useIsNarrow();
    if (panelState !== PANEL_DEFAULT) return filtersOpen;
    return !isNarrow;
}
